import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Mail, Send, Check, AlertCircle } from "lucide-react";

const EMPTY = {
  host: "",
  port: 587,
  username: "",
  password: "",
  from_email: "",
  from_name: "Fila Inteligente",
  recipient_email: "",
  use_tls: true,
  enabled: true,
  password_set: false,
};

const SmtpTab = () => {
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/platform/smtp");
      if (data && data.host) {
        setForm({ ...EMPTY, ...data, password: "" });
        setTestEmail(data.recipient_email || "");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        host: form.host.trim(),
        port: Number(form.port),
        username: form.username.trim(),
        from_email: form.from_email.trim().toLowerCase(),
        from_name: form.from_name.trim() || "Fila Inteligente",
        recipient_email: form.recipient_email.trim().toLowerCase(),
        use_tls: !!form.use_tls,
        enabled: !!form.enabled,
      };
      // Only send password if user typed a new one
      if (form.password && form.password.trim()) {
        payload.password = form.password.trim();
      }
      const { data } = await api.put("/platform/smtp", payload);
      setForm({ ...EMPTY, ...data, password: "" });
      toast.success("Configuração SMTP salva");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/platform/smtp/test", {
        to_email: testEmail || undefined,
      });
      toast.success(`Email de teste enviado para ${data.to}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return <div className="text-foreground/50">Carregando…</div>;

  const presets = [
    { label: "Gmail", host: "smtp.gmail.com", port: 587, use_tls: true },
    { label: "Outlook 365", host: "smtp.office365.com", port: 587, use_tls: true },
    { label: "SendGrid", host: "smtp.sendgrid.net", port: 587, use_tls: true },
    { label: "AWS SES (us-east-1)", host: "email-smtp.us-east-1.amazonaws.com", port: 587, use_tls: true },
    { label: "SMTP SSL (465)", host: "", port: 465, use_tls: false },
  ];

  const applyPreset = (p) => {
    setForm((prev) => ({ ...prev, host: p.host || prev.host, port: p.port, use_tls: p.use_tls }));
  };

  return (
    <div className="max-w-3xl" data-testid="smtp-tab">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Notificações</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">
          Configuração SMTP
        </h1>
        <p className="text-sm text-foreground/60 mt-3 max-w-xl">
          Configure o servidor de envio de emails para receber notificações automáticas quando
          alguém preencher o formulário "Solicitar acesso" na landing page pública.
        </p>
      </div>

      {form.password_set ? (
        <div className="border border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 mb-6 flex items-center gap-2 text-sm">
          <Check className="w-4 h-4 text-emerald-600" />
          <span>Senha SMTP armazenada. Deixe o campo "Senha" vazio para manter a senha atual.</span>
        </div>
      ) : (
        <div className="border border-amber-600 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 mb-6 flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <span>Nenhuma senha SMTP configurada. Defina uma antes de testar o envio.</span>
        </div>
      )}

      <div className="mb-6">
        <Label className="text-xs uppercase tracking-[0.2em] text-foreground/60">Presets rápidos</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {presets.map((p) => (
            <Button
              key={p.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset(p)}
              className="rounded-none text-xs"
              data-testid={`smtp-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <form onSubmit={save} className="space-y-5 border border-border bg-background p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Servidor (host)*</Label>
            <Input
              value={form.host}
              onChange={(e) => setForm((p) => ({ ...p, host: e.target.value }))}
              className="mt-2 h-11 rounded-none font-mono"
              placeholder="smtp.gmail.com"
              data-testid="smtp-host"
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Porta*</Label>
            <Input
              type="number"
              value={form.port}
              onChange={(e) => setForm((p) => ({ ...p, port: e.target.value }))}
              className="mt-2 h-11 rounded-none font-mono"
              data-testid="smtp-port"
              min={1}
              max={65535}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Usuário (login)</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              className="mt-2 h-11 rounded-none font-mono"
              placeholder="seuemail@gmail.com"
              data-testid="smtp-username"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">
              Senha {form.password_set && <span className="normal-case font-normal text-foreground/50">(deixe vazio p/ manter)</span>}
            </Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              className="mt-2 h-11 rounded-none font-mono"
              placeholder={form.password_set ? "••••••••" : "App password"}
              data-testid="smtp-password"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">From email*</Label>
            <Input
              type="email"
              value={form.from_email}
              onChange={(e) => setForm((p) => ({ ...p, from_email: e.target.value }))}
              className="mt-2 h-11 rounded-none font-mono"
              placeholder="noreply@suaempresa.com"
              data-testid="smtp-from-email"
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">From name</Label>
            <Input
              value={form.from_name}
              onChange={(e) => setForm((p) => ({ ...p, from_name: e.target.value }))}
              className="mt-2 h-11 rounded-none"
              data-testid="smtp-from-name"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-[0.2em]">Email destinatário (recebe os leads)*</Label>
          <Input
            type="email"
            value={form.recipient_email}
            onChange={(e) => setForm((p) => ({ ...p, recipient_email: e.target.value }))}
            className="mt-2 h-11 rounded-none font-mono"
            placeholder="rsilva@licensehub.com.br"
            data-testid="smtp-recipient"
            required
          />
        </div>

        <div className="flex items-center justify-between border-t border-border pt-5">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!form.use_tls}
              onCheckedChange={(v) => setForm((p) => ({ ...p, use_tls: v }))}
              data-testid="smtp-use-tls"
            />
            <Label className="text-sm cursor-pointer" onClick={() => setForm((p) => ({ ...p, use_tls: !p.use_tls }))}>
              Usar STARTTLS (recomendado para porta 587)
            </Label>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!form.enabled}
              onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: v }))}
              data-testid="smtp-enabled"
            />
            <Label className="text-sm cursor-pointer" onClick={() => setForm((p) => ({ ...p, enabled: !p.enabled }))}>
              <strong>Envio ativo</strong> — desligue para parar de receber notificações
            </Label>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-none h-12 px-6 uppercase tracking-[0.2em] text-sm"
            data-testid="smtp-save-btn"
          >
            {saving ? "Salvando…" : "Salvar configuração"}
          </Button>
        </div>
      </form>

      <div className="mt-8 border border-border p-6 md:p-8 bg-muted/30">
        <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3 flex items-center gap-2">
          <Send className="w-3 h-3" /> Teste de envio
        </div>
        <p className="text-sm text-foreground/60 mb-4">
          Envia um email de teste para confirmar que a configuração funciona. Salve as alterações antes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="email-para-teste@exemplo.com (vazio = usa o destinatário)"
            className="h-11 rounded-none font-mono"
            data-testid="smtp-test-email"
          />
          <Button
            type="button"
            onClick={sendTest}
            disabled={testing || !form.host}
            variant="outline"
            className="rounded-none h-11 uppercase tracking-[0.2em] text-xs"
            data-testid="smtp-test-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> {testing ? "Enviando…" : "Enviar teste"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SmtpTab;
